import { reportModuleLoading } from "@client/actions";
import { getModuleData } from "@client/content/dataProvider";
import { makeModuleStateSelector } from "@client/content/selectors";
import Loading from "@client/controls/Loading/component";
import { usePrevious } from "@client/lib/hooks";
import { connectCustom } from "@client/lib/misc";
import { type BaseAppState, type EntityRef, Status } from "@client/types";
import { type WithChildren } from "@common/declarations";
import React from "react";
import { type Dispatch } from "redux";

type Init = (data: EntityRef) => void;
type ComponentMapProps = EntityRef &
  WithChildren & {
    moduleType: string;
    status: string;
  };

type ComponentActionProps = {
  init: (fn: Init) => void;
  onInitialized: () => void;
};
const Component = (props: ComponentActionProps & ComponentMapProps) => {
  React.useEffect(() => {
    if (props.status === Status.None) {
      const mi = getModuleData(props.moduleType);

      mi?.init && props.init(mi.init);
    }
  }, [props.id, props.moduleType, props.status, props.init]);

  const previousStatus = usePrevious(props.status);
  React.useEffect(() => {
    if (previousStatus === Status.Loading && props.status !== Status.None && props.status !== Status.Loading) {
      props.onInitialized();
    }
  }, [props.onInitialized, previousStatus, props.status]);

  if (props.status === Status.None) return null;
  if (props.status === Status.Loading) return <Loading isTransparent />;

  return props.children;
};

type Props = Omit<ComponentMapProps, "init" | "status">;
const InitContainer = connectCustom(
  () => {
    const moduleStateSelector = makeModuleStateSelector<{ status: string }>({ status: "Ready" }, { status: "Ready" });

    return (state: BaseAppState, props: Props): ComponentMapProps => ({
      ...props,
      status: moduleStateSelector(state, props)?.status ?? Status.Ready
    });
  },
  (dispatch: Dispatch<any>, props: Props): ComponentActionProps => ({
    init: (fn: Init) => {
      dispatch(reportModuleLoading({ isLoading: true, moduleId: props.id }));
      dispatch(fn({ id: props.id, source: props.source }));
    },
    onInitialized: () => dispatch(reportModuleLoading({ isLoading: false, moduleId: props.id }))
  })
)(Component);
export default InitContainer;
